import React, { Component } from "react";
import { Modal } from "react-bootstrap";
import EventEmitter from "../../../services/event-emitter";
import NumberFormat from "react-number-format";
import AbbreviateNumber from "../../../components/ui-building-blocks/numbers/AbbreviateNumber";
import {
  batchRequest,
  postRequest,
  putRequest,
  deleteRequest
} from "../../../api/ApiService";
import { logError } from "../../../services/utils";
import { renderToStaticMarkup } from "react-dom/server";
import _ from "lodash";
import HelpIconButtonWithTooltipAndTypeformModal from "../../typeform/HelpIconButtonWithTooltipAndTypeformModal";
import { Loader } from "react-loaders";
import {
  incrementUnacceptedDecisions,
  decrementUnacceptedDecisions
} from "../../../api/SubmitDecisions";
import TypeformContent from "../../typeform/TypeformContent";
import SuccessAlert from "../../alerts/success";
import WarningAlert from "../../alerts/warning";
import ErrorInlineAlert from "../../alerts/errorInline";
import Fragment from 'react-dot-fragment';

const DEBT_CLIENTS_URL = `${process.env.REACT_APP_API_URL}static/debt_clients/`;
const DEBTS_URL = `${process.env.REACT_APP_API_URL}team/debts/`;
const DEBT_CONDITIONS_URL = `${process.env.REACT_APP_API_URL}static/debt_clients/CLIENT_ID/get_debt_conditions/`;

class RaiseDebtModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      show: false,
      clients: [],
      term: 0,
      amount: 0,
      rate: 0,
      client_id: 0,
      showAlert: false,
      loading: true,
      saving: {},
      max_amounts: {},
      successMessage: null,
      errorMessage: null,
      interestCharge: 0,
      interestChargeWarningCanBeShown: false
    };

    this.eventemitter = EventEmitter.getInstance();
  }

  showSuccessMessage = successMessage => {
    this.setState({
      successMessage,
    }, () => {
      this.hideSuccessMessage();
    });
  }

  hideSuccessMessage = () => {
    setTimeout(() => {
      this.setState({
        successMessage: null,
      });
    }, 5000);
  }

  showErrorMessage = errorMessage => {
    this.setState({
      errorMessage,
    }, () => {
      this.hideErrorMessage();
    });
  }

  hideErrorMessage = () => {
    setTimeout(() => {
      this.setState({
        errorMessage: null,
      });
    }, 5000);
  }

  fetchData = (callback) => {
    const { roundNumber } = this.props;

    batchRequest([
      DEBT_CLIENTS_URL + `?round_number=${roundNumber}`,
      DEBTS_URL + `?round_number=${roundNumber}`
    ])
      .then(res => {
        if (
          res[0].statusCode !== 200 ||
          res[1].statusCode !== 200
        ) {
          throw res;
        }

        const debt_clients = res[0].body;
        const debts = res[1].body;

        let clients = debt_clients.map(client => {
          const client_id = parseInt(client.id, 10);
          const { name } = client;
          const debt = debts.find(i => i.client === client_id && i.term === 8);

          return {
            client_id,
            name,
            term: debt ? debt.term : 8,
            amount: debt ? debt.amount : 0,
            balance: debt ? debt.balance : 0,
            new_amount: debt ? debt.new_amount : 0,
            is_prev: debt && debt.prev_round_amount !== 0 ? true : false,
            prev_amount: debt ? debt.prev_round_amount : 0,
            debt_id: debt ? debt.id : 0,
            rate: debt ? debt.rate : 0
          };
        });

        const interestCharge = debts.reduce((acc, item) => {
          return acc + item.interest_charge;
        }, 0);

        this.setState(
          {
            clients,
            loading: false,
            interestCharge
          },
          () => {
            this.loadMaxAmounts();
            if (callback) {
              callback();
            }
          }
        );
      })
      .catch(err => {
        logError(err, "RaiseDebtModal", "fetchData");
      });
  };

  getMaximumCapital = client_id => {
    const { max_amounts } = this.state;

    if (client_id in max_amounts) {
      return Number(max_amounts[client_id]);
    }

    return 0;
  };

  getMaximumCapitalFixed = client_id => {
    return Math.floor(this.getMaximumCapital(client_id));
  };

  getMaxAmount = client_id => {
    const { clients } = this.state;
    const client = clients.find(i => i.client_id === client_id);
    const term = client ? client.term : 0;

    return postRequest(
      `${process.env.REACT_APP_API_URL}static/debt_clients/${client_id}/get_max_amount/`
    ).send({ term });
  };

  loadMaxAmounts = () => {
    let requests = [];
    let clients = _.cloneDeep(this.state.clients);
    let max_amounts = {};

    clients.forEach(client => {
      requests.push(this.getMaxAmount(client.client_id));
    });

    if (requests.length > 0) {
      const joinRequest = Promise.all(requests);

      joinRequest
        .then(res => {
          res.forEach(obj => {
            const path = obj.req.url.split("/");
            const client_id = Number(path[6]);
            max_amounts[client_id] = obj.body.max_amount;
          });

          this.setState({
            max_amounts
          });
        })
        .catch(err => {
          logError(err, "RaiseDebtModal", "loadMaxAmounts");
        });
    }
  };

  setSavingState = (client_id, value) => {
    let saving = _.cloneDeep(this.state.saving);
    saving[client_id] = value;

    this.setState({
      saving
    });
  };

  getSavingState = client_id => {
    const { saving } = this.state;

    if (client_id in saving) {
      return saving[client_id];
    }

    return false;
  };

  get maturity() {
    const { term } = this.state;

    return term === 1 ? "1 year" : "8 years";
  }

  get amountFormatted() {
    const { amount } = this.state;
    const val = (amount / 1000000).toFixed(2);

    return `$${val}M`;
  }

  get interestRate() {
    const { rate } = this.state;
    const val = (rate * 100).toFixed(2);

    return `${val}%`;
  }

  get alertText() {
    return (
      <div>
        <b>
          Based on your Capital Structure, we are going
          to offer you the following:
        </b>
        <br /><br />
        Maturity {this.maturity}
        <br />
        Amount {this.amountFormatted}
        <br />
        Interest Rate {this.interestRate}
      </div>
    );
  }

  errorText = ({ message }) => {
    return renderToStaticMarkup(
      <div>
        <b>{message}</b>
      </div>
    );
  };

  showAlert = () => {
    this.setState({
      showAlert: true,
    });
  };

  hideAlert = () => {
    const { client_id } = this.state;

    this.setState(
      {
        showAlert: false
      },
      () => {
        this.setSavingState(client_id, false);
      }
    );
  };

  close = () => {
    const { showInterestChargeWarning } = this.props;
    const { interestChargeWarningCanBeShown } = this.state;
    this.setState({
      show: false
    }, () => {
      if (interestChargeWarningCanBeShown) {
        showInterestChargeWarning();
        this.setState({
          interestChargeWarningCanBeShown: false
        });
      }
    });
  };

  show = () => {
    this.setState(
      {
        show: true
      },
      () => {
        this.fetchData();
      }
    );
  };

  componentDidMount() {
    this.eventemitter.on("show-raise-debt-modal", this.show);
  }

  componentWillUnmount() {
    this.eventemitter.off("show-raise-debt-modal", this.show);
  }

  changeAmount = (e, value, client_id) => {
    let clients = _.cloneDeep(this.state.clients);
    const val =
      "floatValue" in value ? value.floatValue : Number(e.target.value);
    const index = clients.findIndex(i => i.client_id === client_id);

    if (index >= 0) {
      clients[index].new_amount = val;

      this.setState({
        clients
      });
    }
  };

  onDelete = (client_id, debt_id) => {
    const { reloadData } = this.props;

    this.setSavingState(client_id, true);

    deleteRequest(DEBTS_URL + debt_id + "/")
      .then(res => {
        this.setSavingState(client_id, false);
        this.fetchData();
        reloadData();
      })
      .catch(err => {
        logError(err, "RaiseDebtModal", "onDelete");
      });
  };

  onSave = client_id => {
    const { equity } = this.props;
    const { clients } = this.state;
    const client = clients.find(i => i.client_id === client_id);

    if (!client) {
      return;
    }

    if (equity === 0) {
      this.showErrorMessage('Show us some equity and we\'ll give you some debt. Please raise equity.');
      return;
    }

    if (client.new_amount < 0) {
      this.showErrorMessage('Input Capital Request must be 0 or greater than 0.');
      return;
    }

    const max_amount = this.getMaximumCapital(client_id);
    const validation_amount = Math.floor(max_amount + client.amount);
    if (client.prev_amount + client.new_amount > validation_amount) {
      const message =
        client.is_prev === true
          ? "Your loan size cannot exceed the maximum capital."
          : "You've exceed the maximum capital allowed. Please adjust loan amount.";

      this.showErrorMessage(message);
      return;
    }

    this.setSavingState(client_id, true);

    postRequest(DEBT_CONDITIONS_URL.replace("CLIENT_ID", client_id))
      .send({
        amount: client.prev_amount + client.new_amount,
        term: client.term
      })
      .then(conditions => {
        const { term, rate } = conditions.body;

        this.setState(
          {
            term,
            amount: client.new_amount,
            rate,
            client_id
          },
          () => {
            this.showAlert();
          }
        );
      })
      .catch(err => {
        logError(err, "RaiseDebtModal", "onSave");
      });
  };

  getDebtsUrl = () => {
    const { clients, client_id } = this.state;
    const client = clients.find(i => i.client_id === client_id);
    const debt_id = client ? client.debt_id : 0;
    const url = `${process.env.REACT_APP_API_URL}team/debts/`;

    return debt_id > 0 ? putRequest(`${url}${debt_id}/`) : postRequest(url);
  };

  submit = () => {
    const { client_id, term, clients } = this.state;
    const { reloadData } = this.props;
    const client = clients.find(i => i.client_id === client_id);
    const amount = client ? client.prev_amount + client.new_amount : 0;

    const { interestCharge } = this.state;
    if (amount === 0) {
      decrementUnacceptedDecisions("finance");
    } else {
      incrementUnacceptedDecisions("finance");
    }
    this.showSuccessMessage((
      <Fragment>
        <b>{(client.new_amount).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
        })}</b> of debt capital raised successfully
      </Fragment>
    ));

    this.hideAlert();
    if (interestCharge > 2500000) {
      this.setState({ interestChargeWarningCanBeShown: true })
    }
    this.getDebtsUrl()
      .send({
        client: client_id,
        term,
        amount
      })
      .then(res => {

        this.fetchData();
        reloadData();
      })
      .catch((err, res) => {
        const message = _.get(err, ["response", "body", "amount", 0]);
        this.showErrorMessage(message);
        logError(err, "RaiseDebtModal", "submit");
      });
  };

  getRaisedCapitalFixed = client => {
    return client ? Math.floor(client.balance) : 0;
  };

  render() {
    const { show,
      clients,
      showAlert,
      loading,
      errorMessage,
      successMessage
    } = this.state;

    const style = {
      zIndex: 100,
      position: "relative"
    };

    return show ? (
      <Fragment>
        {showAlert && <WarningAlert
          title="ARE YOU SURE?"
          message={this.alertText}
          onSuccess={this.submit}
          onCancel={this.hideAlert}
        />}
        {!showAlert && (
          <Modal
            show={true}
            onHide={this.close}
            dialogClassName="modal-dialog-bigger-more fn-modal-dialog"
          >
            <Modal.Header closeButton={true}>
              <p className="modal-p text-align-left ml-10" style={style}>
                <b>Loans</b>
                <HelpIconButtonWithTooltipAndTypeformModal
                  iconSize="xs"
                  {...TypeformContent.DEBT_VS_EQUITY}
                />
              </p>
            </Modal.Header>
            {loading === true && (
              <Modal.Body>
                <div className="text-center loader-inline">
                  <Loader type="ball-scale-ripple" active />
                </div>
              </Modal.Body>
            )}
            {loading === false && (
              <Modal.Body>
                {successMessage && <SuccessAlert
                  message={successMessage}
                  marginTop={1}
                  marginBottom={80}
                />}
                {errorMessage && <ErrorInlineAlert
                  title="Error"
                  message={errorMessage}
                  marginTop={1}
                  marginBottom={80}
                />}
                <div className="table-responsive fn-decisions-raise-debt">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>&nbsp;</th>
                        {clients.map(client => (
                          <th
                            key={client.client_id}
                            dangerouslySetInnerHTML={{
                              __html: client.name.replace(" ", "<br />")
                            }}
                          />
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="no-align select-maturity">
                          Select Maturity
                        </td>
                        {clients.map(client => (
                          <td key={client.client_id}>
                            8 years
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="no-align">Borrowing Limit</td>
                        {clients.map(client => (
                          <td key={client.client_id}>
                            <NumberFormat
                              value={this.getMaximumCapitalFixed(client.client_id)}
                              displayType="text"
                              prefix="$"
                              thousandSeparator=","
                            />
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="no-align input-capital-request">
                          Input Loan Request
                        </td>
                        {clients.map(client => (
                          <td key={client.client_id}>
                            <AbbreviateNumber
                              type="input"
                              value={client.new_amount}
                              onChange={(e, value) =>
                                this.changeAmount(e, value, client.client_id)
                              }
                            />
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td>&nbsp;</td>
                        {clients.map(client => {
                          const saving = this.getSavingState(client.client_id);

                          return (
                            <td key={client.client_id}>
                              <button
                                type="button"
                                className="btn btn-save"
                                onClick={() => this.onSave(client.client_id)}
                                disabled={saving}
                              >
                                {saving === true ? "Loading..." : "Save"}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                      <tr>
                        <td className="no-align capital-raised">Loan Balance</td>
                        {clients.map(client => (
                          <td key={client.client_id} className="capital-raised">
                            <NumberFormat
                              value={this.getRaisedCapitalFixed(client)}
                              displayType="text"
                              prefix="$"
                              thousandSeparator=","
                            />
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="no-align">Lending Rate</td>
                        {clients.map(client => (
                          <td key={client.client_id} className="lending-rate">
                            {client.debt_id > 0 && client.balance > 0 && (
                              <NumberFormat
                                value={(client.rate * 100).toFixed(2)}
                                displayType="text"
                                suffix="%"
                                thousandSeparator=","
                              />
                            )}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div style={{ textAlign: "right" }}>
                  <button
                    type="button"
                    className="btn btn-close-modal"
                    onClick={this.close}
                  >
                    Close
                  </button>
                </div>
              </Modal.Body>
            )}
          </Modal>
        )}
      </Fragment>
    ) : null
  }
}

export default RaiseDebtModal;



// WEBPACK FOOTER //
// ./src/components/finance/decisions/raise-debt-modal.js